import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import { RegistryEntryResponse } from '../../types';

interface RegistryEntryDetailProps {
  modelId: number;
  entryId: string | null;
  onClose: () => void;
}

/**
 * RegistryEntryDetail - Modal component for viewing full entry details
 *
 * Features:
 * - Full output_value JSON display
 * - Source agent and run instance information
 * - Loading state while fetching
 * - Close button to dismiss modal
 */
const RegistryEntryDetail: React.FC<RegistryEntryDetailProps> = ({
  modelId,
  entryId,
  onClose
}) => {
  const [entry, setEntry] = useState<RegistryEntryResponse | null>(null);
  const [loading, setLoading] = useState(!!entryId);

  const loadEntry = useCallback(async (id: string) => {
    try {
      setLoading(true);
      const data = await apiService.getRegistryEntry(modelId, id);
      setEntry(data);
    } finally {
      setLoading(false);
    }
  }, [modelId]);

  useEffect(() => {
    if (entryId) {
      loadEntry(entryId);
    } else {
      setEntry(null);
      setLoading(false);
    }
  }, [entryId, loadEntry]);

  // Don't render anything if no entry is selected
  if (!entryId) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-cap-dark p-6 rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Entry Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 text-2xl leading-none"
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>

        {loading ? (
          <div className="text-center py-4">Loading...</div>
        ) : entry ? (
          <div className="space-y-4">
            {/* Output Value Section */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Output Value</h3>
              <pre className="bg-gray-100 dark:bg-gray-700 p-3 rounded text-sm overflow-x-auto">
                {JSON.stringify(entry.output_value, null, 2)}
              </pre>
            </div>

            {/* Source Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Source Agent</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Agent ID: {entry.source_agent_id ?? 'N/A'}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Source Run</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Run Instance ID: {entry.source_run_instance_id ?? 'N/A'}
                </p>
              </div>
            </div>

            {/* Timestamps */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Created At</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {entry.created_at ? new Date(entry.created_at).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Expires At</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {entry.expires_at ? new Date(entry.expires_at).toLocaleString() : 'Never'}
                </p>
              </div>
            </div>

            {/* Hash */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dedup Key Hash</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-mono break-all">
                {entry.dedup_key_hash}
              </p>
            </div>
          </div>
        ) : null}

        {/* Close Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegistryEntryDetail;
